<template>
    <div
        v-if="shouldShow"
        class="onboarding"
        :class="{ active: user }"
    >
        <div class="card">
            <div class="header">
                <h3>Welcome! 👋</h3>
                <button @click="hide">
                    ×
                </button>
            </div>

            <div class="progress">
                <div
                    class="bar"
                    :style="{ width: progress + '%' }"
                />
                <span>{{ Math.round(progress) }}%</span>
            </div>

            <div class="steps">
                <div
                    v-for="(step, i) in steps"
                    :key="step.name"
                    class="step"
                    :class="{ done: isDone(step.name), current: i === current }"
                >
                    <div class="num">
                        {{ isDone(step.name) ? '✓' : i + 1 }}
                    </div>
                    <div>
                        <div class="step-header">
                            <span>{{ step.label }}</span>
                            <a
                                v-if="step.name === 'watch_intro_video' && isDone(step.name)"
                                href="https://docs.livecaller.io/livecaller/"
                                target="_blank"
                                class="doc-link"
                            >
                                📖 View Documentation
                            </a>
                            <a
                                v-if="step.name === 'install_widget' && isDone(step.name)"
                                href="/widgets"
                                class="doc-link"
                            >
                                🔧 Manage Widgets
                            </a>
                            <a
                                v-if="step.name === 'receive_first_conversation'"
                                href="https://youtu.be/jFkEn6Ylpfw"
                                target="_blank"
                                class="doc-link"
                            >
                                📹 How to install widget
                            </a>
                        </div>
                        <button
                            v-if="i === current && !isDone(step.name)"
                            class="btn"
                            @click="action(step.name)"
                        >
                            {{ getButtonText(step.name) }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="showVideo"
            class="video"
            @click="closeVideo"
        >
            <div @click.stop>
                <button @click="closeVideo">
                    ×
                </button>
                <iframe
                    :src="videoSrc"
                    width="800"
                    height="450"
                    frameborder="0"
                    allowfullscreen
                />
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, computed, getCurrentInstance } from 'vue'
    import { LiveCaller } from '@/LiveCaller'
    import useToast from '@/shared/toast/useToast'
    import { useAuthStore } from '@/stores/auth'

    export default {
        setup() {
            const { proxy } = getCurrentInstance()
            const { showError } = useToast()
            const showVideo = ref(false)
            const videoSrc = ref('https://www.youtube.com/embed/pthB_5YbkCk')

            const steps = [
                { name: 'create_account', label: 'Create Account' },
                { name: 'watch_intro_video', label: 'Watch Video' },
                { name: 'install_widget', label: 'Get Code' },
                { name: 'receive_first_conversation', label: 'First Chat' },
            ]

            const authStore = useAuthStore()
            const user = computed(() => authStore.user)
            const completed = computed(() => user.value?.account?.onboarding?.completed_steps || [])
            const progress = computed(() => (completed.value.length / steps.length) * 100)
            const current = computed(() => completed.value.length)
            const shouldShow = computed(() => user.value && progress.value < 100)

            const isDone = (name) => completed.value.includes(name)
            const getButtonText = (name) => ({
                watch_intro_video: '▶ Watch',
                install_widget: '→ Code',
            })[name] || '→ Next'

            const complete = async () => {
                const stepName = steps[current.value]?.name
                if (!stepName) return
                try {
                    await LiveCaller.request().patch(`/account/onboarding/${stepName}/mark-as-completed`)
                    await authStore.load(true)
                } catch (e) {
                    showError('Error completing step')
                }
            }

            const closeVideo = () => {
                showVideo.value = false
                videoSrc.value = 'https://www.youtube.com/embed/pthB_5YbkCk'
                complete()
            }

            const openVideo = () => {
                showVideo.value = true
                videoSrc.value = 'https://www.youtube.com/embed/pthB_5YbkCk?autoplay=1'
                setTimeout(() => showVideo.value && closeVideo(), 30000)
            }

            const goWidget = async () => {
                try {
                    const res = await LiveCaller.request({ url: 'widgets', method: 'get' })
                    await proxy.$router.push(`/widget/${res.data.data[0].id}/edit`)
                    complete()
                } catch (e) {
                    console.error(e)
                }
            }

            const action = (name) => {
                if (name === 'watch_intro_video') openVideo()
                else if (name === 'install_widget') goWidget()
                else complete()
            }

            const hide = () => {
                const el = document.querySelector('.onboarding')
                if (el) el.classList.add('hidden')
            }

            return {
                user,
                steps,
                progress,
                current,
                shouldShow,
                showVideo,
                videoSrc,
                isDone,
                getButtonText,
                action,
                closeVideo,
                hide,
            }
        },
    }
</script>

<style scoped>
.onboarding {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 1000;
    display: none;
    align-items: center;
    justify-content: center;
    padding: 20px;
}
.onboarding.active { display: flex; }
.onboarding.hidden { display: none !important; }

.card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
    max-width: 400px;
    width: 100%;
    overflow: hidden;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eee;
}
.header h3 { margin: 0; font-size: 18px; }
.header button {
    background: #f5f5f5;
    border: none;
    border-radius: 4px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    font-size: 16px;
}

.progress {
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
}
.bar {
    flex: 1;
    height: 4px;
    background: #5DA3FF;
    border-radius: 2px;
    transition: width 0.3s;
}
.progress span { font-size: 12px; color: #5DA3FF; font-weight: 500; }

.steps { padding: 0 20px 20px; }
.step {
    display: flex;
    gap: 10px;
    padding: 8px 0;
    align-items: flex-start;
}
.step.current { background: #f8faff; margin: 0 -10px; padding: 8px 10px; border-radius: 6px; }
.step.done { opacity: 0.6; }

.num {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}
.step.done .num { background: #5DA3FF; color: white; }
.step.current:not(.done) .num { background: #5DA3FF; color: white; }
.step:not(.current):not(.done) .num { background: #f0f0f0; color: #666; }

.step > div:last-child { flex: 1; }

.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.step-header span {
    font-size: 14px;
    font-weight: 500;
}

.step.done .step-header span {
    text-decoration: line-through;
    color: #666;
}

.btn {
    background: #5DA3FF;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    cursor: pointer;
}
.btn:hover { background: #4299e1; }

.doc-link {
    color: #5DA3FF;
    text-decoration: none;
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.doc-link:hover { text-decoration: underline; }

.video {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.9);
    z-index: 1100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}
.video > div {
    position: relative;
    max-width: 800px;
    width: 100%;
    aspect-ratio: 16/9;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
}
.video button {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    background: rgba(0,0,0,0.7);
    border: none;
    border-radius: 50%;
    color: white;
    cursor: pointer;
    z-index: 10;
}
.video iframe { width: 100%; height: 100%; }
</style>
